import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  CartResponse,
  updateCartItems,
  deleteCartItem,
  updateCartLanguage,
  removeEnrollmentCartItem,
  recalculateCart,
  updateCartItemVariant,
} from "@/networking/carts.api";

import { updateAddress, Address } from "@/networking/addresses.api";
import { updateCartCountry } from "@/networking/addresses.api";
import { updateSubscription } from "@/networking/subscriptions.api";
import { addAddressToCart } from "@/networking/addresses.api";
import { updateShippingMethod } from "@/networking/shipping.api";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { isApiError } from "@/lib/api-helpers";

const FALLBACK_REMOVE_ITEM_ERROR =
  "We couldn't remove that item. Please try again or contact support.";

function extractApiErrorMessage(err: unknown): string {
  if (isApiError(err) && err.data?.error_message) {
    return err.data.error_message;
  }
  return FALLBACK_REMOVE_ITEM_ERROR;
}

export function useCartMutations(
  cartToken: string,
  paymentMethodsIncludes: string | null,
) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const cartQueryKey = ["cart", cartToken, paymentMethodsIncludes];

  const setCartData = (response: CartResponse) => {
    queryClient.setQueryData<CartResponse>(cartQueryKey, (old) => ({
      ...response,
      meta: {
        ...old?.meta,
        ...response.meta,
      },
    }));
  };

  const recalculateCartMutation = useMutation({
    mutationFn: async (): Promise<CartResponse> => {
      return recalculateCart(cartToken, paymentMethodsIncludes);
    },
    onSuccess: (response) => {
      queryClient.cancelQueries({
        queryKey: cartQueryKey,
      });
      setCartData(response);
    },
    onError: (error) => {
      console.error("Error recalculating cart:", error);
    },
  });

  const deleteItem = useMutation({
    mutationFn: async ({ itemId }: { itemId: number }) => {
      return deleteCartItem(cartToken, itemId);
    },
    onMutate: async ({ itemId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: cartQueryKey,
      });

      // Snapshot the previous value
      const previousCart = queryClient.getQueryData<CartResponse>([
        "cart",
        cartToken,
        paymentMethodsIncludes,
      ]);

      // Optimistically remove item from cart
      queryClient.setQueryData<CartResponse>(cartQueryKey, (old) => {
        if (!old) return old;
        const newCart: CartResponse = {
          ...old,
          cart: {
            ...old.cart,
            items: old.cart.items.filter((item) => item.id !== itemId),
          },
        };
        return newCart;
      });

      return { previousCart };
    },
    onError: (err, _itemId, context) => {
      // Rollback on error
      if (context?.previousCart) {
        queryClient.setQueryData(cartQueryKey, context.previousCart);
      }
      // Surface the backend error (e.g. authorized-cart 410, gateway-void
      // 422). Without this the optimistic update silently reverts and the
      // user thinks the trash button is broken (CURRENT-1427).
      toast.error(extractApiErrorMessage(err));
    },
    onSuccess: (response) => {
      setCartData(response);
    },
  });

  const removeEnrollmentItem = useMutation({
    mutationFn: async ({
      itemId,
      options,
    }: {
      itemId: string;
      options?: {
        fluidShop?: string;
        fluidSession?: string;
      };
    }) => {
      return removeEnrollmentCartItem(cartToken, itemId, options);
    },
    onMutate: async ({ itemId }) => {
      await queryClient.cancelQueries({
        queryKey: cartQueryKey,
      });

      const previousCart = queryClient.getQueryData<CartResponse>([
        "cart",
        cartToken,
        paymentMethodsIncludes,
      ]);

      queryClient.setQueryData<CartResponse>(cartQueryKey, (old) => {
        if (!old) return old;
        const newCart: CartResponse = {
          ...old,
          cart: {
            ...old.cart,
            items: old.cart.items.filter(
              (item) => item.id !== parseInt(itemId),
            ),
          },
        };
        return newCart;
      });

      return { previousCart };
    },
    onError: (err, _variables, context) => {
      if (context?.previousCart) {
        queryClient.setQueryData(cartQueryKey, context.previousCart);
      }
      // Surface the backend error so the user sees what went wrong instead
      // of the item silently snapping back into the cart (CURRENT-1427).
      toast.error(extractApiErrorMessage(err));
    },
    onSuccess: (response) => {
      setCartData(response);
    },
  });

  const updateItemQuantity = useMutation({
    mutationKey: ["update-item-quantity", cartToken],
    mutationFn: async ({
      variantId,
      quantity,
      itemId,
    }: {
      variantId?: number;
      quantity: number;
      itemId: number;
    }) => {
      if (!variantId) {
        throw new Error("Cannot update quantity for items without a variant");
      }
      // Get current cart to check for free items
      const currentCart = queryClient.getQueryData<CartResponse>([
        "cart",
        cartToken,
        paymentMethodsIncludes,
      ]);

      if (currentCart) {
        // Find free items with the same variant_id
        const freeItemsWithSameVariant = currentCart.cart.items.filter(
          (item) =>
            item.variant?.id === variantId &&
            item.id !== itemId &&
            item.free_item,
        );

        // If there are free items, send the actual quantity requested
        // The backend should handle the free item relationship automatically
        if (freeItemsWithSameVariant.length > 0) {
          return updateCartItems(cartToken, [
            {
              variant_id: variantId,
              quantity: quantity,
            },
          ]);
        }
      }

      // Normal update for items without free items
      return updateCartItems(cartToken, [
        {
          variant_id: variantId,
          quantity,
        },
      ]);
    },
    onMutate: async () => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: cartQueryKey,
      });
    },
    onSuccess: (response) => {
      // Always use the server response to ensure consistency
      setCartData(response);
    },
    onError: (error) => {
      console.error("Quantity update error:", error);
    },
  });

  const updateAddressMutation = useMutation({
    mutationFn: async (addressBody: Address) => {
      return updateAddress(addressBody, cartToken);
    },
    onMutate: async (addressBody) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: cartQueryKey,
      });

      // Snapshot the previous value
      const previousCart = queryClient.getQueryData<CartResponse>([
        "cart",
        cartToken,
        paymentMethodsIncludes,
      ]);
      // Optimistically update cart address
      queryClient.setQueryData<CartResponse>(cartQueryKey, (old) => {
        if (!old) return old;
        const newCart: CartResponse = {
          ...old,
          cart: {
            ...old.cart,
            [addressBody.type === "ship_to" ? "ship_to" : "bill_to"]:
              addressBody.address,
          },
        };
        return newCart;
      });

      return { previousCart };
    },
    onError: (error, _variables, context) => {
      console.error("Address update error:", error);
      if (context?.previousCart) {
        queryClient.setQueryData(cartQueryKey, context.previousCart);
      }
    },
    onSuccess: (response) => {
      setCartData(response);
    },
  });

  const updateLanguage = useMutation({
    mutationFn: async (body: { language_iso: string }) => {
      return updateCartLanguage(cartToken, body);
    },
    onMutate: async (body) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: cartQueryKey,
      });

      // Snapshot the previous value
      const previousCart = queryClient.getQueryData<CartResponse>([
        "cart",
        cartToken,
        paymentMethodsIncludes,
      ]);

      // Optimistically update cart language
      queryClient.setQueryData<CartResponse>(cartQueryKey, (old) => {
        if (!old) return old;
        const newCart: CartResponse = {
          ...old,
          cart: {
            ...old.cart,
            language_iso: body.language_iso,
          },
        };
        return newCart;
      });

      return { previousCart };
    },
    onError: (error) => {
      console.error("Language update error:", error);
    },
    onSuccess: async (response) => {
      setCartData(response);
      // Refresh API-rendered labels after the cart language is saved.
      await queryClient.invalidateQueries({ queryKey: cartQueryKey });
    },
  });

  const updateCountry = useMutation({
    mutationFn: async (body: { country_code: string }) => {
      return updateCartCountry(cartToken, { country_code: body.country_code });
    },
    onMutate: async (body) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: cartQueryKey,
      });

      // Snapshot the previous value
      const previousCart = queryClient.getQueryData<CartResponse>([
        "cart",
        cartToken,
        paymentMethodsIncludes,
      ]);

      // Optimistically update cart country
      queryClient.setQueryData<CartResponse>(cartQueryKey, (old) => {
        if (!old) return old;
        const newCart: CartResponse = {
          ...old,
          cart: {
            ...old.cart,
            country: {
              ...old.cart.country,
              iso: body.country_code,
            },
          },
        };
        return newCart;
      });

      return { previousCart };
    },
    onError: (error, _variables, context) => {
      console.error("Country update error:", error);
      if (context?.previousCart) {
        queryClient.setQueryData(cartQueryKey, context.previousCart);
      }
    },
    onSuccess: (response) => {
      setCartData(response);
    },
  });

  const updateItemSubscription = useMutation({
    mutationKey: ["update-item-subscription", cartToken],
    mutationFn: async ({
      itemId,
      subscription,
      subscriptionPlanId,
    }: {
      itemId: number;
      subscription: boolean;
      subscriptionPlanId?: number;
    }) => {
      return updateSubscription(
        cartToken,
        itemId,
        subscription,
        subscriptionPlanId,
      );
    },
    onMutate: async ({ itemId, subscription }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: cartQueryKey,
      });

      // Snapshot the previous value
      const previousCart = queryClient.getQueryData<CartResponse>([
        "cart",
        cartToken,
        paymentMethodsIncludes,
      ]);

      // Optimistically update cart subscription status and price
      queryClient.setQueryData<CartResponse>(cartQueryKey, (old) => {
        if (!old) return old;
        const newCart: CartResponse = {
          ...old,
          cart: {
            ...old.cart,
            items: old.cart.items.map((item) => {
              if (item.id !== itemId) return item;

              const isCurrentlySubscribed = item.subscription;
              if (isCurrentlySubscribed === subscription) {
                return item;
              }

              const subscribeAndSave = parseFloat(
                item.subscribe_and_save || "0",
              );
              const currentPrice = parseFloat(item.price);

              let newPrice = currentPrice;
              if (subscribeAndSave > 0) {
                if (isCurrentlySubscribed) {
                  newPrice = currentPrice + subscribeAndSave;
                } else {
                  newPrice = currentPrice - subscribeAndSave;
                }
              }

              return {
                ...item,
                subscription,
                price: newPrice.toFixed(2),
              };
            }),
          },
        };
        return newCart;
      });
      return { previousCart };
    },
    onError: (error: unknown, _variables, context) => {
      toast.error(t("subscription_cannot_be_changed"));
      console.error("Subscription update error:", error);
      if (context?.previousCart) {
        queryClient.setQueryData(cartQueryKey, context.previousCart);
      }
    },
    onSuccess: (response) => {
      setCartData(response);
    },
  });

  const addAddressToCartMutation = useMutation({
    mutationFn: async ({
      type,
      jwt,
      address_id,
    }: {
      type: string;
      jwt: string;
      address_id: string;
    }) => {
      return addAddressToCart(cartToken, type, jwt, address_id);
    },
    onMutate: async ({ type, address_id }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: cartQueryKey,
      });

      // Snapshot the previous value
      const previousCart = queryClient.getQueryData<CartResponse>([
        "cart",
        cartToken,
        paymentMethodsIncludes,
      ]);

      // Optimistically update cart address
      queryClient.setQueryData<CartResponse>(cartQueryKey, (old) => {
        if (!old) return old;
        const newCart: CartResponse = {
          ...old,
          cart: {
            ...old.cart,
            [type === "ship_to" ? "ship_to" : "bill_to"]: {
              ...old.cart[type === "ship_to" ? "ship_to" : "bill_to"],
              id: Number(address_id),
            },
          },
        };
        return newCart;
      });

      return { previousCart };
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({
        queryKey: cartQueryKey,
      });
    },
    onError: (error, _variables, context) => {
      console.error("Add address to cart error:", error);
      if (context?.previousCart) {
        queryClient.setQueryData(cartQueryKey, context.previousCart);
      }
    },
  });

  const updateShippingMethodMutation = useMutation({
    mutationFn: ({ shippingMethodId }: { shippingMethodId: string }) =>
      updateShippingMethod({ cartToken, shippingMethodId }),
    onMutate: async ({ shippingMethodId }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: cartQueryKey,
      });

      // Snapshot the previous value
      const previousCart = queryClient.getQueryData<CartResponse>([
        "cart",
        cartToken,
        paymentMethodsIncludes,
      ]);

      // Optimistically update shipping method
      queryClient.setQueryData<CartResponse>(cartQueryKey, (old) => {
        if (!old) return old;
        return {
          ...old,
          cart: {
            ...old.cart,
            shipping_method: {
              id: shippingMethodId,
              name: old.cart.shipping_method?.name || null,
            },
          },
        };
      });

      return { previousCart };
    },
    onError: (error, _variables, context) => {
      console.error("Error updating shipping method:", error);
      if (context?.previousCart) {
        queryClient.setQueryData(cartQueryKey, context.previousCart);
      }
    },
    onSuccess: (response: CartResponse) => {
      setCartData(response);
    },
  });

  const updateCartItemVariantMutation = useMutation({
    mutationFn: ({
      itemId,
      variantId,
      quantity,
    }: {
      itemId: number;
      variantId: number;
      quantity: number;
    }) => updateCartItemVariant(cartToken, itemId, variantId, quantity),
    onMutate: async ({ itemId, variantId, quantity }) => {
      await queryClient.cancelQueries({
        queryKey: cartQueryKey,
      });
      const previousCart = queryClient.getQueryData<CartResponse>([
        "cart",
        cartToken,
        paymentMethodsIncludes,
      ]);
      queryClient.setQueryData<CartResponse>(cartQueryKey, (old) => {
        if (!old) return old;
        const newCart: CartResponse = {
          ...old,
          cart: {
            ...old.cart,
            items: old.cart.items.map((item) =>
              item.id === itemId && item.variant
                ? {
                    ...item,
                    variant: { ...item.variant, id: variantId },
                    quantity: quantity,
                  }
                : item,
            ),
          },
        };
        return newCart;
      });
      return { previousCart };
    },
    onSuccess: (response) => {
      setCartData(response);
    },
    onError: (error, _variables, context) => {
      console.error("Error updating cart item variant:", error);
      if (context?.previousCart) {
        queryClient.setQueryData(cartQueryKey, context.previousCart);
      }
      toast.error(t("variant_update_failed"));
    },
  });

  return {
    deleteItem,
    removeEnrollmentItem,
    updateItemQuantity,
    updateAddressMutation,
    updateLanguage,
    updateCountry,
    updateItemSubscription,
    addAddressToCartMutation,
    updateShippingMethodMutation,
    recalculateCartMutation,
    updateCartItemVariantMutation,
  };
}
